import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCheck, Loader2, OctagonX, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { backLabel } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { CheckItem, isStop } from "@/components/fleet/CheckItem";
import { HandoffNote } from "@/components/fleet/BikeCards";
import { BikeStatus } from "@/components/fleet/BikeCards";

const SECTIONS = [["bike", "Bike checks"], ["kit", "Kit on the bike"], ["question", "Questions"]];

const BikePicker = ({ bikes, value, onChange, meId }) => (
  <div className="grid grid-cols-2 gap-3">
    {bikes.map((b) => {
      const other = b.booked_now && b.booked_now.user_id !== meId;
      const blocked = b.status === "off_road" || !!b.current_run_id || other;
      return (
        <button key={b.id} type="button" disabled={blocked} onClick={() => onChange(b)} data-testid={`pick-bike-${b.id}`}
          className={`space-y-2 rounded-2xl border-2 p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${value?.id === b.id ? "border-primary bg-primary/10" : "border-border hover:border-zinc-500"}`}>
          <div className="font-extrabold">{b.name}</div>
          <div className="font-mono text-xs text-muted-foreground">{b.registration || "Reg not set"}</div>
          <BikeStatus bike={b} />
          {b.status === "off_road" && <div data-testid={`pick-bike-back-${b.id}`} className="text-[11px] font-semibold text-sky-300">{backLabel(b)}</div>}
          {other && <div className="text-[11px] font-semibold text-amber-300">Booked by {b.booked_now.user_name} until {b.booked_now.end.slice(11)}</div>}
          {b.booked_now?.user_id === meId && <div className="text-[11px] font-semibold text-emerald-300">Your booking</div>}
        </button>
      );
    })}
  </div>
);

export default function BikeCheck() {
  const { runId } = useParams();
  const stage = runId ? "post" : "pre";
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [run, setRun] = useState(null);
  const [bike, setBike] = useState(null);
  const [ack, setAck] = useState(false);
  const noteRef = useRef(null);
  useEffect(() => {
    setAck(false);
    noteRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [bike?.id]);
  const [mileage, setMileage] = useState("");
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [block, setBlock] = useState("");
  const [params] = useSearchParams();

  useEffect(() => {
    if (runId) return;
    api.get("/fleet/eligibility").then(({ data }) => {
      if (data.open_run) nav(`/bike-check/${data.open_run.id}/post`, { replace: true });
      else if (!data.eligible) setBlock(data.reason);
    });
  }, [runId, nav]);

  useEffect(() => {
    api.get("/check-items").then((r) => setItems(r.data.filter((i) => i.active && [stage, "both"].includes(i.stage))));
    if (runId) api.get(`/runs/${runId}`).then((r) => setRun(r.data));
    else api.get("/bikes").then((r) => {
      setBikes(r.data);
      const pre = r.data.find((b) => b.id === params.get("bike") && b.status !== "off_road" && !b.current_run_id);
      if (pre) setBike(pre);
    });
  }, [runId, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = useMemo(() => items.filter((i) => answers[i.id]?.answer).length, [items, answers]);
  const allOk = () => {
    const next = { ...answers };
    items.forEach((i) => { if (!next[i.id]?.answer && i.kind === "check") next[i.id] = { answer: "ok", note: "", photos: [] }; });
    setAnswers(next);
  };

  const submit = async () => {
    if (stage === "pre" && !bike) return toast.error("Choose a bike");
    if (mileage === "") return toast.error(`Enter the ${stage === "pre" ? "start" : "end"} mileage`);
    const missing = items.filter((i) => i.mandatory && (i.kind === "text" ? !answers[i.id]?.note?.trim() : !answers[i.id] || answers[i.id].answer === "not_done"));
    if (missing.length) return toast.error(`Required: ${missing.map((i) => i.label).join(", ")}`);
    const noNote = items.find((i) => ["fault", "no"].includes(answers[i.id]?.answer) && !answers[i.id].note?.trim());
    if (noNote) return toast.error(`Describe the issue for: ${noNote.label}`);
    if (!confirmed) return toast.error("Confirm you have completed ALL of the checks");
    if (stage === "pre" && bike?.handoff_note && !ack) return toast.error("Tick 'I've read this' on the repair note before starting");
    setBusy(true);
    const body = { mileage: Number(mileage), answers, confirmed, general_notes: notes, handoff_ack: ack };
    try {
      const { data } = stage === "pre" ? await api.post("/runs", { ...body, bike_id: bike.id }) : await api.post(`/runs/${runId}/post`, body);
      toast.success(stage === "pre" ? `Pre-ride check saved. Safe riding on ${data.bike_name}!` : "Post-ride check complete. Bike returned.", { description: data.taken_off_road ? `${data.bike_name} has been taken OFF ROAD (under repair) because of a safety-critical fault.` : undefined });
      nav(`/runs/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const faults = items.filter((i) => ["fault", "no"].includes(answers[i.id]?.answer)).length;
  const stops = items.filter((i) => isStop(i, answers[i.id], stage));
  const reportOnly = async () => {
    if (!bike) return toast.error("Choose a bike");
    const noNote = items.find((i) => ["fault", "no"].includes(answers[i.id]?.answer) && !answers[i.id].note?.trim());
    if (noNote) return toast.error(`Describe the issue for: ${noNote.label}`);
    setBusy(true);
    try {
      const { data } = await api.post(`/bikes/${bike.id}/report-faults`, { answers });
      toast.success(`${data.reported} fault${data.reported > 1 ? "s" : ""} reported on ${data.bike_name}. Do not take this bike.`, { description: data.taken_off_road ? `${data.bike_name} has been taken OFF ROAD (under repair) automatically.` : undefined });
      nav("/fleet", { replace: true });
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (block) return <p data-testid="not-eligible-msg" className="mx-auto max-w-2xl rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">{block}</p>;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">{stage === "pre" ? "Before you ride" : "On return"}</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">{stage === "pre" ? "Pre-ride check" : `Post-ride check · ${run?.bike_name || ""}`}</h1>
      </div>

      <section className="space-y-4 rounded-2xl border bg-card p-5">
        {stage === "pre" ? <><Label>Choose bike</Label><BikePicker bikes={bikes} value={bike} onChange={setBike} meId={user.id} /></> : run && <p className="text-sm text-muted-foreground">Start mileage: <span className="font-mono text-foreground">{run.pre.mileage}</span></p>}
        {stage === "pre" && bike?.handoff_note && <div ref={noteRef}><HandoffNote note={bike.handoff_note} testid="check-handoff-note" />
          <label className={`mt-2 flex cursor-pointer items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-sm font-semibold ${ack ? "border-emerald-500/50 bg-emerald-500/10" : "border-sky-500/50"}`}>
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="h-5 w-5 accent-emerald-500" data-testid="handoff-ack-checkbox" /> I've read this repair note
          </label></div>}
        <div className="space-y-1.5">
          <Label>{stage === "pre" ? "Start miles" : "End miles"}</Label>
          <Input type="number" inputMode="numeric" min={0} className="h-12 max-w-[200px] font-mono" value={mileage} onChange={(e) => setMileage(e.target.value)} data-testid="check-mileage-input" />
        </div>
      </section>

      <div className="sticky top-[64px] z-30 -mx-4 flex items-center justify-between gap-3 bg-background/90 px-4 py-3 backdrop-blur-xl lg:top-0">
        <span data-testid="check-progress" className="text-sm"><span className="font-mono font-bold text-primary">{done}/{items.length}</span> checked{faults > 0 && <span className="ml-2 font-semibold text-red-400">· {faults} issue{faults > 1 ? "s" : ""}</span>}</span>
        <button type="button" onClick={allOk} data-testid="mark-all-ok-btn" className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold hover:bg-secondary"><CheckCheck className="h-4 w-4" /> Mark remaining OK</button>
      </div>

      {SECTIONS.map(([k, title]) => {
        const list = items.filter((i) => i.section === k);
        if (!list.length) return null;
        return (
          <section key={k} className="overflow-hidden rounded-2xl border bg-card" data-testid={`check-section-${k}`}>
            <h3 className="border-b px-5 py-3 text-sm font-bold uppercase tracking-widest text-muted-foreground">{title}</h3>
            {list.map((i) => <CheckItem key={i.id} item={i} stage={stage} value={answers[i.id]} onChange={(v) => setAnswers((a) => ({ ...a, [i.id]: v }))} />)}
          </section>
        );
      })}

      {stops.length > 0 ? (
        <section data-testid="stop-ride-panel" className="space-y-3 rounded-2xl border-2 border-red-500 bg-red-600/15 p-5">
          <div className="flex items-center gap-2 text-lg font-extrabold text-red-200"><OctagonX className="h-6 w-6 text-red-400" /> Do not take this bike</div>
          <ul className="list-disc space-y-1 pl-6 text-sm text-red-100">{stops.map((i) => <li key={i.id}><b>{i.label}</b>: {i.stop_message || "failed safety check"}</li>)}</ul>
          <p className="text-xs text-red-200/80">Describe each fault above (photos help), then report it. The bike is taken off road automatically and the Fleet Manager will see it straight away.</p>
          <button type="button" disabled={busy} onClick={reportOnly} data-testid="report-fault-btn" className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-destructive font-bold text-white disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Report fault - don't take bike
          </button>
        </section>
      ) : (
      <section className="space-y-4 rounded-2xl border bg-card p-5">
        <Textarea rows={2} placeholder="Any other comments (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="check-general-notes" />
        <label className="flex items-start gap-3 text-sm font-semibold">
          <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5 h-5 w-5" data-testid="check-confirm-checkbox" />
          I confirm I have completed ALL of the above checks
        </label>
        <p className="text-xs text-muted-foreground">Faults, damage or missing items are reported to the Bike / Fleet Manager automatically.</p>
        <button type="button" disabled={busy} onClick={submit} data-testid="check-submit-btn" className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-destructive font-bold text-white hover:bg-red-700 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {stage === "pre" ? "Submit & take bike" : "Submit & return bike"}
        </button>
      </section>
      )}
    </div>
  );
}
